// Direct sun on a window at the winter solstice, with the building's own shadow.
//
// The OMS 119/2014 test is hours of direct sun in living rooms on 21
// December, the shortest and lowest day. In Romania the sun then rises in
// the south-east, peaks at ~21–25° and sets in the south-west, so a facade
// facing north gets none at all and a courtyard narrower than its walls are
// tall gets little, which is the physics behind two of an architect's
// first objections. Plan coordinates: x east, y north.
import type { MultiPoly, Pt } from "./geom.ts";

const RAD = Math.PI / 180;
const DECLINATION = -23.44; // winter solstice
/** Below this the sun is too low to count: horizon haze, trees, kerbs. */
export const MIN_ALTITUDE = 3;
/** A window does not see the sun past this angle off its normal, in plan. */
const MAX_INCIDENCE = 85;
const STEP_MIN = 10;

export interface SunSample { azimuth: number; altitude: number } // degrees; azimuth from north, clockwise

/** Sun positions through the winter solstice, every 10 minutes of solar time, while it is up. */
export function solsticeSun(latitude: number): SunSample[] {
  const phi = latitude * RAD, dec = DECLINATION * RAD;
  const out: SunSample[] = [];
  for (let m = 0; m < 24 * 60; m += STEP_MIN) {
    const h = ((m - 720) / 4) * RAD; // hour angle, 15° an hour
    const sinAlt = Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(h);
    const alt = Math.asin(sinAlt) / RAD;
    if (alt < MIN_ALTITUDE) continue;
    const az = (Math.atan2(Math.sin(h), Math.cos(h) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi)) / RAD + 180 + 360) % 360;
    out.push({ azimuth: az, altitude: alt });
  }
  return out;
}

/** Azimuth of a plan direction (x east, y north), degrees clockwise from north. */
export const azimuthOf = (v: Pt) => (Math.atan2(v[0], v[1]) / RAD + 360) % 360;

/** A block that can cast a shadow: a footprint between two heights. */
export interface Mass { footprint: MultiPoly; base: number; top: number }

/** Distance along a ray from p in direction d to where it first enters the polygon set, or Infinity. */
function entry(p: Pt, d: Pt, mp: MultiPoly): number {
  let best = Infinity;
  for (const poly of mp) for (const ring of poly) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      const ex = b[0] - a[0], ey = b[1] - a[1];
      const den = d[0] * ey - d[1] * ex;
      if (Math.abs(den) < 1e-12) continue;
      const t = ((a[0] - p[0]) * ey - (a[1] - p[1]) * ex) / den;
      const u = ((a[0] - p[0]) * d[1] - (a[1] - p[1]) * d[0]) / den;
      if (t > 1e-6 && u >= 0 && u <= 1) best = Math.min(best, t);
    }
  }
  return best;
}

/**
 * Hours of direct sun on a window centred at `at`, `height` metres up,
 * facing `normal`, on 21 December. Only the masses given cast shadows:
 * today that is the building itself, because neighbours are not known.
 */
export function sunHours(at: Pt, height: number, normal: Pt, masses: Mass[], sun: SunSample[]): number {
  const facing = azimuthOf(normal);
  // start just outside the facade, so the wall the window is in does not shade it
  const p: Pt = [at[0] + normal[0] * 0.05, at[1] + normal[1] * 0.05];
  let lit = 0;
  for (const s of sun) {
    const off = Math.abs(((s.azimuth - facing + 540) % 360) - 180);
    if (off >= MAX_INCIDENCE) continue;
    const d: Pt = [Math.sin(s.azimuth * RAD), Math.cos(s.azimuth * RAD)];
    const rise = Math.tan(s.altitude * RAD);
    const shaded = masses.some((m) => {
      const t = entry(p, d, m.footprint);
      if (!Number.isFinite(t)) return false;
      const z = height + t * rise;
      return z < m.top && z > m.base - 0.01;
    });
    if (!shaded) lit++;
  }
  return (lit * STEP_MIN) / 60;
}

/** Latitude from a Stereo 70 northing, to a tenth of a degree, which is all the sun needs. */
export function latitudeFromStereo70(north: number): number {
  return 46 + (north - 500_000) / 111_200;
}

/** Bucharest, the default when nothing better is known. */
export const DEFAULT_LATITUDE = 44.43;
